import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ["position", "subject", "from", "received", "snippet", "body", "emptyState", "content", "prevLink", "nextLink", "originalLoader", "draftLoader", "bodyWrapper", "sendButton", "gmailLink", "markActionedButton"];
    static values = {
        indexUrl: String,
        showUrlTemplate: String,
        updateUrlTemplate: String,
        sendUrlTemplate: String,
        discardUrlTemplate: String,
        actionedUrlTemplate: String,
        dashboardUrl: String,
        selectedId: Number
    };

    connect() {
        this.drafts = [];
        this.currentIndex = 0;
        this.draftCache = new Map();
        this.loadingIndexPromise = null;
        this.sendButtonDefaultHTML = this.hasSendButtonTarget ? this.sendButtonTarget.innerHTML : "Send Now";
        this.markActionedButtonDefaultHTML = this.hasMarkActionedButtonTarget ? this.markActionedButtonTarget.innerHTML : "Mark as done";
        this.fetchIndex(); // kick off preload
        this.selectedIdFromValue = this.hasSelectedIdValue ? this.selectedIdValue : null;
    }

    reset() {
        this.drafts = [];
        this.currentIndex = 0;
        this.bodyTarget.value = "";
        this.showEmptyState(true);
        this.clearStatus();
    }

    async loadDraft(index, { force = false } = {}) {
        this.currentIndex = index;
        const meta = this.drafts[index];
        if (!meta) return;
        this.resetLoadingStates();
        this.showEmptyState(false);
        this.showSkeleton(true);
        try {
            const data = await this.fetchDraftData(meta.id, { force });
            if (this.currentIndex !== index && !force) return;
            this.renderDraft(data, index);
            this.updateNavState();
            this.prefetchNext(index);
        } catch (e) {
            this.showStatus("Could not load this draft.", "danger");
        } finally {
            this.showSkeleton(false);
        }
    }

    renderDraft(data, index) {
        this.positionTarget.textContent = `${index + 1} of ${this.drafts.length} drafts`;
        this.subjectTarget.textContent = data.subject || "(No subject)";
        this.fromTarget.textContent = data.from || "(Unknown sender)";
        this.receivedTarget.textContent = data.received_at ? new Date(data.received_at).toLocaleString() : "";
        this.snippetTarget.textContent = data.snippet || data.draft_preview || "";
        this.bodyTarget.value = data.draft_body || data.draft_preview || "";
        this.setGmailLink(data);
    }

    async save() {
        const meta = this.drafts[this.currentIndex];
        if (!meta) return;
        const body = this.bodyTarget.value;
        if (!body.trim()) {
            this.showStatus("Draft body cannot be blank.", "warning");
            return;
        }
        try {
            const res = await fetch(this.urlFor(this.updateUrlTemplateValue, meta.id), {
                method: "PATCH",
                headers: this.jsonHeaders(),
                body: JSON.stringify({ draft_body: body })
            });
            if (!res.ok) throw new Error("save failed");
            this.showStatus("Draft saved.", "success");
        } catch (e) {
            this.showStatus("Could not save draft.", "danger");
        }
    }

    async send() {
        const meta = this.drafts[this.currentIndex];
        if (!meta) return;
        this.setSendingState(true);
        try {
            const res = await fetch(this.urlFor(this.sendUrlTemplateValue, meta.id), {
                method: "POST",
                headers: this.jsonHeaders(),
                body: JSON.stringify({ draft_body: this.bodyTarget.value })
            });
            if (!res.ok) throw new Error("send failed");
            await res.json();
            this.removeCurrent("Draft sent.");
        } catch (e) {
            this.showStatus("Could not send draft.", "danger");
            this.setSendingState(false);
        }
    }

    async discard() {
        const meta = this.drafts[this.currentIndex];
        if (!meta) return;
        try {
            const res = await fetch(this.urlFor(this.discardUrlTemplateValue, meta.id), {
                method: "DELETE",
                headers: this.jsonHeaders()
            });
            if (!res.ok) throw new Error("discard failed");
            await res.json();
            this.removeCurrent("Draft discarded.");
        } catch (e) {
            this.showStatus("Could not discard draft.", "danger");
        }
    }

    async markActioned(event) {
        if (event && event.preventDefault) event.preventDefault();
        const meta = this.drafts[this.currentIndex];
        if (!meta) return;
        this.setLoadingState(this.markActionedButtonTarget, true, "Marking…");
        try {
            const res = await fetch(this.urlFor(this.actionedUrlTemplateValue, meta.id), {
                method: "POST",
                headers: this.jsonHeaders(),
                body: JSON.stringify({ format: "json" })
            });
            if (!res.ok) throw new Error("mark_actioned failed");
            await res.json();
            this.removeCurrent("Marked as done.");
        } catch (e) {
            this.showStatus("Could not mark as done.", "danger");
            this.setLoadingState(this.markActionedButtonTarget, false);
        }
    }

    removeCurrent(message) {
        this.drafts.splice(this.currentIndex, 1);
        if (this.drafts.length === 0) {
            this.showEmptyState(true);
            this.positionTarget.textContent = "No drafts";
            this.showStatus(message, "success");
            this.updateNavState();
            try {
                window.localStorage.setItem("draftsAllDoneConfetti", "1");
            } catch (_) {
                /* ignore */
            }
            const redirect = this.hasDashboardUrlValue ? this.dashboardUrlValue : "/";
            window.location = redirect;
            return;
        }
        if (this.currentIndex >= this.drafts.length) {
            this.currentIndex = this.drafts.length - 1;
        }
        this.showStatus(message, "success");
        this.loadDraft(this.currentIndex);
        this.updateNavState();
    }

    next(event) {
        if (event && event.preventDefault) event.preventDefault();
        if (this.drafts.length === 0) return;
        this.currentIndex = (this.currentIndex + 1) % this.drafts.length;
        this.loadDraft(this.currentIndex);
    }

    prev(event) {
        if (event && event.preventDefault) event.preventDefault();
        if (this.drafts.length === 0) return;
        this.currentIndex = (this.currentIndex - 1 + this.drafts.length) % this.drafts.length;
        this.loadDraft(this.currentIndex);
    }

    updateNavState() {
        const multiple = this.drafts.length > 1;
        const prevDisabled = !multiple;
        const nextDisabled = !multiple;
        this.toggleLink(this.prevLinkTarget, prevDisabled);
        this.toggleLink(this.nextLinkTarget, nextDisabled);
    }

    toggleLink(link, disabled) {
        if (!link) return;
        if (disabled) {
            link.classList.add("text-muted", "disabled", "pe-none");
            link.classList.remove("text-primary");
            link.setAttribute("tabindex", "-1");
            link.setAttribute("aria-disabled", "true");
            link.removeAttribute("href");
        } else {
            link.classList.remove("text-muted", "disabled", "pe-none");
            link.classList.add("text-primary");
            link.removeAttribute("tabindex");
            link.setAttribute("aria-disabled", "false");
            link.setAttribute("href", "#");
        }
    }

    setSendingState(sending, label = "Sending…") {
        this.setLoadingState(this.sendButtonTarget, sending, label);
    }

    setLoadingState(button, loading, label = "Loading…") {
        if (!button) return;
        if (loading) {
            button.disabled = true;
            button.innerHTML = `<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>${label}`;
        } else {
            button.disabled = false;
            if (button === this.sendButtonTarget && this.sendButtonDefaultHTML) {
                button.innerHTML = this.sendButtonDefaultHTML;
            } else if (button === this.markActionedButtonTarget && this.markActionedButtonDefaultHTML) {
                button.innerHTML = this.markActionedButtonDefaultHTML;
            }
        }
    }

    resetLoadingStates() {
        this.setLoadingState(this.sendButtonTarget, false);
        this.setLoadingState(this.markActionedButtonTarget, false);
    }

    setGmailLink(data) {
        if (!this.hasGmailLinkTarget) return;
        const threadId = data.thread_id || data.threadId || null;
        if (!threadId) {
            this.gmailLinkTarget.classList.add("disabled", "pe-none", "text-muted");
            this.gmailLinkTarget.removeAttribute("href");
            return;
        }
        const url = `https://mail.google.com/mail/u/0/#all/${threadId}`;
        this.gmailLinkTarget.href = url;
        this.gmailLinkTarget.classList.remove("disabled", "pe-none", "text-muted");
    }

    showSkeleton(show) {
        [this.originalLoaderTarget, this.draftLoaderTarget].forEach((el) => {
            if (!el) return;
            el.classList.toggle("d-none", !show);
        });
        if (this.bodyWrapperTarget) {
            this.bodyWrapperTarget.classList.toggle("d-none", show);
        }
    }

    showStatus(message, level) {
        const el = this.element.querySelector("[data-draft-review-target='status']");
        if (!el) return;
        el.classList.remove("d-none", "alert-info", "alert-success", "alert-warning", "alert-danger");
        el.classList.add(`alert-${level}`);
        el.textContent = message;
    }

    clearStatus() {
        const el = this.element.querySelector("[data-draft-review-target='status']");
        if (!el) return;
        el.classList.add("d-none");
        el.textContent = "";
    }

    showEmptyState(show) {
        if (show) {
            this.emptyStateTarget.classList.remove("d-none");
            this.contentTarget.classList.add("d-none");
        } else {
            this.emptyStateTarget.classList.add("d-none");
            this.contentTarget.classList.remove("d-none");
        }
    }

    async fetchIndex() {
        if (this.loadingIndexPromise) return this.loadingIndexPromise;
        this.loadingIndexPromise = (async () => {
            try {
                const res = await fetch(this.indexUrlValue, { headers: { Accept: "application/json" } });
                const data = await res.json();
                this.drafts = data.drafts || [];
                if (this.drafts.length > 0) {
                    if (this.selectedIdFromValue) {
                        const idx = this.drafts.findIndex((d) => String(d.id) === String(this.selectedIdFromValue));
                        this.currentIndex = idx >= 0 ? idx : 0;
                    } else {
                        this.currentIndex = 0;
                    }
                    this.prefetchDraft(this.currentIndex);
                }
            } catch (e) {
                this.showStatus("Could not load drafts.", "danger");
            } finally {
                this.loadingIndexPromise = null;
                if (this.drafts.length === 0) {
                    this.showEmptyState(true);
                    this.positionTarget.textContent = "No drafts";
                    this.updateNavState();
                } else {
                    this.loadDraft(this.currentIndex, { force: true });
                }
            }
        })();
        return this.loadingIndexPromise;
    }

    async fetchDraftData(id, { force = false } = {}) {
        if (!force && this.draftCache.has(id)) {
            return this.draftCache.get(id);
        }
        const res = await fetch(this.urlFor(this.showUrlTemplateValue, id), { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error("load failed");
        const data = await res.json();
        this.draftCache.set(id, data);
        return data;
    }

    prefetchDraft(index) {
        const meta = this.drafts[index];
        if (!meta) return;
        if (this.draftCache.has(meta.id)) return;
        this.fetchDraftData(meta.id).catch(() => {
            /* ignore prefetch errors */
        });
    }

    prefetchNext(index) {
        if (this.drafts.length < 2) return;
        const nextIndex = (index + 1) % this.drafts.length;
        this.prefetchDraft(nextIndex);
    }

    urlFor(template, id) {
        return template.replace("__ID__", id);
    }

    jsonHeaders() {
        return {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": this.csrfToken()
        };
    }

    csrfToken() {
        const token = document.querySelector("meta[name='csrf-token']");
        return token ? token.content : "";
    }
}

